import {
  costCenterSchema,
  customFieldSchema,
  employeeAccountSchema,
  expenseAccountSchema,
  type Filter,
  memberSchema,
  payableSchema,
  supplierAccountSchema,
  supplierSchema,
  taxAccountSchema,
  teamSchema,
} from '@spendesk/bookkeeping-core-types';
import { produce, type WritableDraft, setAutoFreeze } from 'immer';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { z } from 'zod';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { type PayableId } from 'modules/payable/models';
import { apiUrl } from 'src/core/utils/api';

import { type Sort, type SortFilter } from '../../../prepare-payables/models';

setAutoFreeze(false);

export type Payable = z.infer<typeof payableSchema>;

const enrichedPayableSchema = z.object({
  payables: z.array(payableSchema),
  entities: z.object({
    costCenter: z.record(costCenterSchema),
    customField: z.record(customFieldSchema),
    user: z.record(memberSchema),
    group: z.record(teamSchema),
    supplier: z.record(supplierSchema),
    expenseAccount: z.record(expenseAccountSchema),
    vatAccount: z.record(taxAccountSchema),
    employeeAccountPayable: z.record(employeeAccountSchema),
    supplierAccountPayable: z.record(supplierAccountSchema),
    automation: z.record(z.any()),
    allocationsWithResolvedCounterparties: z.record(z.any()),
    amortisation: z.record(z.any()),
    paymentExpenseBeneficiaries: z.record(z.any()),
  }),
  hasNextPage: z.boolean(),
});

export type EnrichedPayablesResponse = z.infer<typeof enrichedPayableSchema>;

const sortFilterToAPISort = (sort: SortFilter): Sort => {
  switch (sort) {
    case 'date-asc':
      return 'asc';
    case 'date-desc':
      return 'desc';
    default:
      return 'desc';
  }
};

const QUERY_KEY = 'enriched-payables';

export const useEnrichedPayablesQuery = (
  filters: Filter | undefined,
  textualSearch: string = '',
  sort: SortFilter,
  limit: number,
) => {
  const companyId = useCompanyId();
  const sortForAPI = sortFilterToAPISort(sort);

  return useInfiniteQuery<EnrichedPayablesResponse>(
    [QUERY_KEY, companyId, filters, textualSearch, limit, sort],
    async ({ pageParam }) => {
      const response = await fetch(
        apiUrl(`/bookkeeping/payables/search/enriched`, companyId),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filters,
            textualSearch,
            limit,
            sort: sortForAPI,
            after: pageParam ?? undefined,
          }),
        },
      );

      if (response.status !== 200) {
        throw new Error('failed to search payables');
      }
      return enrichedPayableSchema.parse(await response.json());
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: true, // Explicitly enable the query
      getNextPageParam: (lastPage) => {
        if (!lastPage.hasNextPage) {
          return null;
        }
        const last = lastPage.payables.at(-1);
        if (!last) {
          return null;
        }

        return {
          id: last.id,
          creationDate: last.creationDate,
        };
      },
    },
  );
};

type Entities = EnrichedPayablesResponse['entities'];

type CacheData = {
  pages: EnrichedPayablesResponse[];
};
/* eslint-disable no-loop-func */
export const useUpdateEnrichedPayablesQueryCache = () => {
  const queryClient = useQueryClient();

  return function updateCache(
    payableId: PayableId,
    produceNewPayable: (edge: WritableDraft<Payable>) => void,
    produceNewEntities?: (edge: WritableDraft<Entities>) => void,
  ) {
    const backupCache: CacheData[] = [];
    const payablesQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: QUERY_KEY });

    for (const query of payablesQueries) {
      query.setData((oldData: CacheData): CacheData => {
        backupCache.push(oldData);

        const matchingPage = oldData.pages.findIndex((page) =>
          page.payables.some((edge) => edge.id === payableId),
        );

        return produce(oldData, (draft) => {
          if (matchingPage !== -1) {
            const payables = draft.pages[matchingPage].payables;

            payables.forEach((edge) => {
              if (edge.id === payableId) {
                produceNewPayable(edge);
              }
              return edge;
            });
          }

          if (produceNewEntities && matchingPage !== -1) {
            produceNewEntities(draft.pages[matchingPage].entities);
          }
        });
      });
    }

    return () => {
      payablesQueries.forEach((query, index) => {
        query.setData(() => backupCache[index]);
      });
    };
  };
};
